import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle2, PauseCircle, XCircle, Server, Cpu, Globe2, ArrowRight, Receipt } from "lucide-react";
import BalanceWidget from "@/components/BalanceWidget";

const STATUS = {
  active:    { Icon: CheckCircle2, color: "border-signal/60 text-signal" },
  suspended: { Icon: PauseCircle,  color: "border-amber-700 text-amber-400" },
  cancelled: { Icon: XCircle,      color: "border-red-900 text-red-400" },
};

function txnColor(t) {
  if (t.type === "topup")     return "text-signal";
  if (t.type === "deduction") return "text-zinc-300";
  if (t.type === "suspend")   return "text-amber-400";
  if (t.type === "refund")    return "text-blue-400";
  return "text-zinc-300";
}

export default function Dashboard() {
  const { user } = useAuth();
  const [subs, setSubs] = useState([]);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/subscriptions/me").then((r) => setSubs(r.data)),
      api.get("/balance/transactions?limit=15").then((r) => setTxns(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  const stats = useMemo(() => {
    const active = subs.filter((s) => s.status === "active");
    const vps = active.filter((s) => s.service_subtype === "vps" || s.service_subtype === "dedicated").length;
    const apis = active.filter((s) => s.service_subtype === "rest_api").length;
    return { active: active.length, vps, apis };
  }, [subs]);

  return (
    <div data-testid="dashboard-page" className="container-pad py-12 md:py-16">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="overline">// CONTROL PANEL</div>
          <h1 className="mt-3 font-display text-4xl tracking-tight">
            Welcome, <span className="text-signal">{user?.name?.split(" ")[0] || "Customer"}</span>
          </h1>
          <p className="mt-2 text-zinc-400 text-sm">{user?.email}</p>
        </div>
        <div className="flex gap-2 self-start">
          <Link to="/topup" className="btn-ghost h-10 px-4 text-xs font-mono tracking-widest" data-testid="dashboard-topup">
            TOP UP
          </Link>
          <Link to="/catalog" className="btn-signal h-10 px-4 text-xs font-mono tracking-widest" data-testid="dashboard-cta-buy">
            DEPLOY <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-4">
        <div className="md:col-span-4">
          <BalanceWidget variant="card" />
        </div>
        <div className="md:col-span-8 grid grid-cols-3 gap-4">
          {[
            { k: stats.active, v: "ACTIVE SUBSCRIPTIONS" },
            { k: stats.vps, v: "SERVERS RUNNING" },
            { k: stats.apis, v: "API KEYS" },
          ].map((s) => (
            <div key={s.v} className="card-flat p-5 flex flex-col justify-between">
              <div className="overline">{s.v}</div>
              <div className="mt-2 font-mono text-3xl text-white">{s.k}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Active subscriptions */}
      <div className="mt-12">
        <div className="overline mb-4">// ACTIVE SUBSCRIPTIONS</div>
        {loading && <div className="font-mono text-xs text-zinc-500">LOADING…</div>}
        {!loading && subs.length === 0 && (
          <div className="card-flat p-10 text-center">
            <div className="font-mono text-xs text-zinc-500">// NO SUBSCRIPTIONS YET</div>
            <Link to="/catalog" className="btn-signal mt-4 inline-flex h-10 px-4 text-xs font-mono tracking-widest" data-testid="dashboard-empty-cta">
              BROWSE CATALOG <ArrowRight size={14} />
            </Link>
          </div>
        )}
        <div className="grid grid-cols-1 gap-3">
          {subs.map((s) => {
            const Icon = s.service_subtype === "dedicated" ? Cpu
                       : s.service_subtype === "rest_api" ? Globe2 : Server;
            const st = STATUS[s.status] || STATUS.active;
            return (
              <Link
                key={s.id}
                to={`/subscriptions/${s.id}`}
                data-testid={`sub-row-${s.id}`}
                className="card-flat p-5 grid grid-cols-12 items-center gap-4 hover:border-zinc-600 transition-colors"
              >
                <div className="col-span-12 md:col-span-1">
                  <Icon size={18} className="text-signal" />
                </div>
                <div className="col-span-12 md:col-span-5">
                  <div className="font-display text-lg">{s.service_name}</div>
                  <div className="font-mono text-[11px] text-zinc-500 mt-0.5">
                    {s.plan_name} · #{s.id.slice(0, 8).toUpperCase()}
                  </div>
                </div>
                <div className="col-span-6 md:col-span-2">
                  <div className="overline">DAILY</div>
                  <div className="font-mono text-sm text-zinc-200 mt-1">
                    ${s.daily_cost_usd.toFixed(2)}/d
                  </div>
                </div>
                <div className="col-span-6 md:col-span-2">
                  <div className="overline">NEXT BILL</div>
                  <div className="font-mono text-sm text-zinc-200 mt-1">
                    {s.next_billing_at ? new Date(s.next_billing_at).toLocaleDateString() : "—"}
                  </div>
                </div>
                <div className="col-span-12 md:col-span-2 text-left md:text-right">
                  <span className={`badge-mono ${st.color}`}>
                    <st.Icon size={11} className="mr-1" /> {s.status?.toUpperCase()}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Balance transactions */}
      <div className="mt-12">
        <div className="flex items-end justify-between mb-4">
          <div className="overline">// BALANCE LEDGER · LAST 15</div>
          <Link to="/topup" className="font-mono text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white">
            Add credit →
          </Link>
        </div>
        {!loading && txns.length === 0 && (
          <div className="card-flat p-6 font-mono text-xs text-zinc-500 text-center">
            // NO TRANSACTIONS YET
          </div>
        )}
        <div className="card-flat divide-y divide-zinc-800">
          {txns.map((t) => (
            <div key={t.id || t.created_at} className="p-4 grid grid-cols-12 items-center gap-3">
              <div className="col-span-12 md:col-span-1">
                <Receipt size={14} className={txnColor(t)} />
              </div>
              <div className="col-span-12 md:col-span-6">
                <div className="text-sm text-zinc-200">{t.description}</div>
                <div className="font-mono text-[11px] text-zinc-500 mt-0.5 uppercase tracking-widest">
                  {t.type}
                </div>
              </div>
              <div className="col-span-6 md:col-span-2">
                <div className="overline">DATE</div>
                <div className="font-mono text-sm text-zinc-200 mt-1">
                  {t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}
                </div>
              </div>
              <div className="col-span-6 md:col-span-3 text-right">
                <div className={`font-mono text-base ${txnColor(t)}`}>
                  {t.amount > 0 ? "+" : ""}${t.amount.toFixed(2)}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                  Balance ${t.balance_after.toFixed(2)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
